import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'

export default function CallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState('processing') // 'processing', 'success', 'error'
  const [message, setMessage] = useState('正在处理 Spotify 授权...')

  useEffect(() => {
    handleCallback()
  }, [])

  const handleCallback = async () => {
    try {
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const error = searchParams.get('error')

      console.log('🔄 处理 Spotify 回调:', { code: !!code, state, error })

      if (error) {
        throw new Error(`授权错误: ${error}`)
      }

      if (!code) {
        throw new Error('缺少授权码')
      }

      // TODO: 这里将来需要将授权码发送到后端获取访问令牌
      // 现在我们暂时只是显示成功并跳转
      setStatus('success')
      setMessage('Spotify 授权成功！即将跳转...')

      // 保存状态到 localStorage（临时解决方案）
      localStorage.setItem('spotify_auth_code', code)
      localStorage.setItem('spotify_auth_state', state || '')

      // 2秒后跳转到首页
      setTimeout(() => {
        navigate('/', { replace: true })
      }, 2000)

    } catch (error) {
      console.error('❌ 回调处理失败:', error)
      setStatus('error')
      setMessage(`授权失败: ${error.message}`)
      
      // 3秒后跳转到首页
      setTimeout(() => {
        navigate('/', { replace: true })
      }, 3000)
    }
  }

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="text-6xl"
          >
            🎵
          </motion.div>
        )
      case 'success':
        return (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="text-6xl"
          >
            ✅
          </motion.div>
        )
      case 'error':
        return (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="text-6xl"
          >
            ❌
          </motion.div>
        )
      default:
        return '🎵'
    }
  }

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-blue-600'
      case 'success':
        return 'text-green-600'
      case 'error':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-6 max-w-md w-full"
      >
        {/* 状态图标 */}
        <div className="flex justify-center">
          {getStatusIcon()}
        </div>

        {/* 标题 */}
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-2xl font-bold text-gray-800"
        >
          Spotify 授权
        </motion.h1>

        {/* 状态消息 */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className={`text-lg ${getStatusColor()}`}
        >
          {message}
        </motion.p>

        {/* 进度指示器 */}
        {status === 'processing' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="space-y-3"
          >
            <div className="flex justify-center space-x-1">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                  className="w-3 h-3 bg-blue-400 rounded-full"
                />
              ))}
            </div>
            <p className="text-sm text-gray-500">
              请稍等，正在与 Spotify 服务器通信...
            </p>
          </motion.div>
        )}

        {/* 错误时显示重试按钮 */}
        {status === 'error' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="space-y-4"
          >
            <button
              onClick={() => navigate('/', { replace: true })}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
            >
              返回首页
            </button>
            <p className="text-sm text-gray-500">
              授权过程中出现错误，您可以稍后重试
            </p>
          </motion.div>
        )}

        {/* 成功时显示附加信息 */}
        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-sm text-gray-500"
          >
            <p>现在您可以享受更丰富的音乐推荐了！</p>
          </motion.div>
        )}
      </motion.div>
    </div>
  )
}
